import { Add, Close, Warning, Info } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { useMemo } from 'react';

import {
  ConditionType,
  AmountOperator,
  CounterpartyOperator,
  CurrencyDropDown,
  InvoiceExpenseType,
  ExpenseOperator
} from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import {
  ifTextStyle,
  noConditionsAlertStyle,
  noConditionsAlertContentStyle,
  conditionRowContainerStyle,
  ruleFormHeaderStyle,
  ruleStepIndicatorStyle,
  fallbackInfoAlertContentStyle,
  disabledStepIndicatorStyle
} from 'styles/pages/ApprovalRuleFormStyle';

import {
  CONDITION_TYPE_OPTIONS,
  formatOperatorForPreview,
  getConditionOperators
} from '../mappers/conditionsMappers';

export interface ConditionsSectionProps {
  expenseTypes?: InvoiceExpenseType[];
  counterparties?: { id: number; name: string }[];
  currencies?: CurrencyDropDown[];
  defaultCurrencyId?: number;
  isFallback?: boolean;
  stepNumber?: number;
  hasDuplicateConditions?: boolean;
}

export type Condition =
  | {
      id: string;
      conditionType: ConditionType.EXPENSE;
      conditionOperator: ExpenseOperator;
      values: InvoiceExpenseType[];
    }
  | {
      id: string;
      conditionType: ConditionType.COUNTERPARTY;
      conditionOperator: CounterpartyOperator;
      values: { id: number; name: string }[];
    }
  | {
      id: string;
      conditionType: ConditionType.AMOUNT;
      conditionOperator: AmountOperator;
      values: [string, string, string];
    };

// Default condition object
const createDefaultCondition = (): Condition => ({
  id: `condition_${Date.now()}`,
  conditionType: ConditionType.EXPENSE,
  conditionOperator: ExpenseOperator.IS,
  values: []
});

const isSingleExpenseOperator = (operator: ExpenseOperator) => {
  return operator === ExpenseOperator.IS || operator === ExpenseOperator.IS_NOT;
};

const isMultiCounterpartyOperator = (operator: CounterpartyOperator) => {
  return (
    operator === CounterpartyOperator.IS ||
    operator === CounterpartyOperator.IS_ANY_OF
  );
};

const formatExpenseNames = (
  values: { name: string }[],
  translate: (key: string) => string
) => {
  const expenseNames = values.map((v) => v.name);
  if (expenseNames.length <= 2) {
    return expenseNames.join(` ${translate('approvals.or')} `);
  }
  return `${expenseNames.slice(0, 2).join(', ')}, ${translate(
    'approvals.or'
  )} ${expenseNames[expenseNames.length - 1]}`;
};

const formatExpenseValues = (
  condition: Condition,
  translate: (key: string) => string
) => {
  if (condition.conditionType !== ConditionType.EXPENSE) {
    return '';
  }
  if (isSingleExpenseOperator(condition.conditionOperator)) {
    return condition.values[0]?.name || '';
  }
  return formatExpenseNames(condition.values, translate);
};

const formatCounterpartyNames = (values: { id: number; name: string }[]) => {
  if (values.length === 1) {
    return values[0].name;
  }
  return values.map((v) => v.name).join(', ');
};

const formatCounterpartyValues = (condition: Condition) => {
  if (
    condition.conditionType !== ConditionType.COUNTERPARTY ||
    !isMultiCounterpartyOperator(condition.conditionOperator)
  ) {
    return '';
  }
  return formatCounterpartyNames(condition.values);
};

const formatAmountBetween = (
  values: [string, string, string],
  translate: (key: string) => string
) => {
  const amountFrom = values[0] || '0';
  const amountTo = values[1];
  const currency = values[2] || 'EUR';
  if (amountTo) {
    return `${amountFrom} ${translate('labels.and')} ${amountTo} ${currency}`;
  }
  return `${amountFrom} ${translate('labels.and')} ? ${currency}`;
};

const formatAmountValues = (
  condition: Condition,
  translate: (key: string) => string
) => {
  if (condition.conditionType !== ConditionType.AMOUNT) {
    return '';
  }
  if (condition.conditionOperator === AmountOperator.BETWEEN) {
    return formatAmountBetween(condition.values, translate);
  }
  const amount = condition.values[0] || '0';
  const currency = condition.values[1] || 'EUR';
  return `${amount} ${currency}`;
};

const formatConditionValuesForPreview = (
  condition: Condition,
  translate: (key: string) => string
) => {
  if (condition.values.length === 0) {
    return '';
  }

  switch (condition.conditionType) {
    case ConditionType.EXPENSE:
      return formatExpenseValues(condition, translate);
    case ConditionType.COUNTERPARTY:
      return formatCounterpartyValues(condition);
    case ConditionType.AMOUNT:
      return formatAmountValues(condition, translate);
    default:
      return '';
  }
};

export const generateRulePreview = (
  conditions: Condition[],
  translate: (key: string) => string
) => {
  if (conditions.length === 0) {
    return 'no conditions are met';
  }

  const conditionPrefixes = conditions.map((condition, index) => {
    const operator = formatOperatorForPreview(
      condition.conditionOperator,
      translate
    );
    const values = formatConditionValuesForPreview(condition, translate);

    let typeLabel = '';
    switch (condition.conditionType) {
      case ConditionType.EXPENSE:
        typeLabel = translate('approvals.expenseType');
        break;
      case ConditionType.AMOUNT:
        typeLabel = translate('approvals.amount');
        break;
      case ConditionType.COUNTERPARTY:
        typeLabel = translate('approvals.counterparty');
        break;
    }

    return `${index === 0 ? '' : translate('approvals.and') + ' '}${typeLabel} ${operator} ${values}`;
  });

  return conditionPrefixes.join(', ');
};

export const ConditionsSection = ({
  expenseTypes,
  counterparties,
  currencies,
  defaultCurrencyId,
  isFallback = false,
  stepNumber = 2,
  hasDuplicateConditions = false
}: ConditionsSectionProps) => {
  const { translate } = useTranslations();
  const { setFieldValue, errors, setTouched, touched } = useFormikContext<{
    conditions: Condition[];
  }>();
  const [{ value: conditions }] = useField('conditions');

  const hasError = (conditionIndex: number, fieldPath: string): boolean => {
    const conditionsErrors = (errors as any).conditions;
    const conditionsTouched = (touched as any).conditions;

    return (
      !!conditionsErrors?.[conditionIndex]?.[fieldPath] &&
      !!conditionsTouched?.[conditionIndex]?.[fieldPath]
    );
  };

  const markConditionTouched = (index: number, fieldPath?: string) => {
    setTouched({
      conditions: conditions.map((_: Condition, i: number) => {
        const existingTouched = (touched as any).conditions?.[i] || {};
        if (i === index) {
          const newTouched = { ...existingTouched };
          if (fieldPath) {
            newTouched[fieldPath] = true;
          } else {
            newTouched.values = true;
          }
          return newTouched;
        }
        return existingTouched;
      })
    });
  };

  const defaultCurrencyIsoCode = useMemo(() => {
    if (!defaultCurrencyId || !currencies) return currencies?.[0]?.isoCode;
    const defaultCurrency = currencies.find((c) => c.id === defaultCurrencyId);
    return defaultCurrency?.isoCode || currencies?.[0]?.isoCode;
  }, [defaultCurrencyId, currencies]);

  const handleAddCondition = () => {
    const newConditions = [...conditions, createDefaultCondition()];
    setFieldValue('conditions', newConditions);
  };

  const handleRemoveCondition = (index: number) => {
    if (isFallback) return;
    const newConditions = conditions.filter(
      (_c: Condition, i: number) => i !== index
    );
    setFieldValue('conditions', newConditions);
  };

  const handleConditionTypeChange = (index: number, newType: ConditionType) => {
    const newConditions = conditions.map((c: Condition, i: number) => {
      if (i !== index) {
        return c;
      }

      const operator = getConditionOperators(newType)[0].value;

      if (
        newType === ConditionType.EXPENSE ||
        newType === ConditionType.COUNTERPARTY ||
        newType === ConditionType.AMOUNT
      ) {
        if (newType === ConditionType.AMOUNT) {
          return {
            id: c.id,
            conditionType: newType,
            conditionOperator: operator,
            values: ['', '', defaultCurrencyIsoCode || '']
          };
        }
        return {
          id: c.id,
          conditionType: newType,
          conditionOperator: operator,
          values: []
        };
      } else {
        return c;
      }
    });

    setFieldValue('conditions', newConditions);
  };

  const handleOperatorChange = (
    index: number,
    newOperator: ExpenseOperator | AmountOperator | CounterpartyOperator
  ) => {
    const newConditions = conditions.map((c: Condition, i: number) =>
      i === index ? { ...c, conditionOperator: newOperator } : c
    );
    setFieldValue('conditions', newConditions);
  };

  const handleConditionValueChange = (
    index: number,
    newValue:
      | InvoiceExpenseType
      | InvoiceExpenseType[]
      | { id: number; name: string }
      | { id: number; name: string }[]
      | [string, string, string]
  ) => {
    const newConditions = conditions.map((c: Condition, i: number) => {
      if (i !== index) {
        return c;
      }

      switch (c.conditionType) {
        case ConditionType.EXPENSE:
        case ConditionType.COUNTERPARTY:
          return {
            ...c,
            values: Array.isArray(newValue) ? newValue : [newValue]
          } as Condition;
        case ConditionType.AMOUNT:
          return {
            ...c,
            values: newValue as [string, string, string]
          } as Condition;
        default:
          return c;
      }
    });
    setFieldValue('conditions', newConditions);
  };

  const handleAmountValueChange = (
    index: number,
    valueIndex: number,
    newValue: string
  ) => {
    const newConditions = conditions.map((c: Condition, i: number) => {
      if (
        i !== index ||
        c.conditionType !== ConditionType.AMOUNT ||
        valueIndex < 0 ||
        valueIndex > 2
      ) {
        return c;
      }
      const values: [string, string, string] = [...c.values];
      values[valueIndex] = newValue;
      return { ...c, values };
    });
    setFieldValue('conditions', newConditions);
    const fieldPath =
      valueIndex === 0
        ? 'amountFrom'
        : valueIndex === 1
          ? 'amountTo'
          : undefined;
    markConditionTouched(index, fieldPath);
  };

  const handleAmountFieldBlur = (index: number, valueIndex: number) => {
    const fieldPath =
      valueIndex === 0
        ? 'amountFrom'
        : valueIndex === 1
          ? 'amountTo'
          : undefined;
    markConditionTouched(index, fieldPath);
  };

  const handleAutocompleteBlur = (index: number) => {
    markConditionTouched(index, 'values');
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '1rem',
        border: '1px solid #e5e7eb'
      }}
    >
      <Box sx={{ ...ruleFormHeaderStyle, opacity: isFallback ? 0.6 : 1 }}>
        <Box
          sx={{
            ...(isFallback
              ? disabledStepIndicatorStyle
              : ruleStepIndicatorStyle),
            opacity: isFallback ? 0.6 : 1
          }}
        >
          {stepNumber}
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            opacity: isFallback ? 0.6 : 1
          }}
        >
          {translate('approvals.conditions')}
        </Typography>
      </Box>
      {!isFallback && hasDuplicateConditions && (
        <Box sx={noConditionsAlertStyle}>
          <Box sx={noConditionsAlertContentStyle}>
            <Warning sx={{ color: '#ed6c02' }} />
            <Typography
              variant="body2"
              sx={{
                color: '#663c00',
                margin: '12px 0'
              }}
            >
              {translate('errors.duplicateCondition')}
            </Typography>
          </Box>
        </Box>
      )}
      {isFallback ? (
        <Box sx={noConditionsAlertStyle}>
          <Box sx={fallbackInfoAlertContentStyle}>
            <Info sx={{ color: '#1976D2' }} />
            <Typography
              variant="body2"
              sx={{
                color: '#014361',
                margin: '4px 0',
                fontWeight: 500
              }}
            >
              {translate('approvals.fallbackNoConditionsInfo')}
            </Typography>
          </Box>
        </Box>
      ) : conditions.length === 0 ? (
        <Box sx={noConditionsAlertStyle}>
          <Box sx={noConditionsAlertContentStyle}>
            <Warning sx={{ color: '#ed6c02' }} />
            <Typography
              variant="body2"
              sx={{
                color: '#663c00',
                margin: '12px 0'
              }}
            >
              {translate('approvals.noConditionsHelp')}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column">
          {conditions.map((condition: Condition, index: number) => (
            <Box key={condition.id} sx={conditionRowContainerStyle}>
              <Typography sx={ifTextStyle}>
                {index === 0
                  ? translate('approvals.if')
                  : translate('approvals.and')}
              </Typography>
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  value={condition.conditionType}
                  onChange={(e: SelectChangeEvent) =>
                    handleConditionTypeChange(
                      index,
                      e.target.value as ConditionType
                    )
                  }
                >
                  {CONDITION_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {translate(option.labelKey)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  value={condition.conditionOperator}
                  onChange={(e) =>
                    handleOperatorChange(
                      index,
                      e.target.value as
                        | ExpenseOperator
                        | AmountOperator
                        | CounterpartyOperator
                    )
                  }
                >
                  {getConditionOperators(condition.conditionType).map(
                    (option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {translate(option.labelKey)}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>

              {condition.conditionType === ConditionType.EXPENSE && (
                <Box sx={{ flex: 2 }}>
                  {condition.conditionOperator === ExpenseOperator.IS ||
                  condition.conditionOperator === ExpenseOperator.IS_NOT ? (
                    <Autocomplete
                      options={expenseTypes || []}
                      getOptionLabel={(option) => option.name}
                      value={condition.values[0] || null}
                      onChange={(_, value) => {
                        handleConditionValueChange(index, value ? [value] : []);
                        markConditionTouched(index);
                      }}
                      onBlur={() => handleAutocompleteBlur(index)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={`${translate(
                            'labels.selectExpenseTypePlaceholder'
                          )}*`}
                          size="small"
                          error={hasError(index, 'values')}
                        />
                      )}
                    />
                  ) : (
                    <Autocomplete
                      multiple
                      options={expenseTypes || []}
                      getOptionLabel={(option) => option.name}
                      value={condition.values || []}
                      onChange={(_, value) => {
                        handleConditionValueChange(index, value || []);
                        markConditionTouched(index);
                      }}
                      onBlur={() => handleAutocompleteBlur(index)}
                      renderValue={(selected) =>
                        selected.map((option: { id: number; name: string }) => (
                          <Chip
                            key={option.id}
                            label={option.name}
                            size="small"
                            sx={{
                              color: '#078dee',
                              borderRadius: '0.25rem'
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={`${translate(
                            'labels.selectExpenseTypesPlaceholder'
                          )}*`}
                          size="small"
                          error={hasError(index, 'values')}
                        />
                      )}
                    />
                  )}
                </Box>
              )}

              {condition.conditionType === ConditionType.COUNTERPARTY && (
                <Box sx={{ flex: 2 }}>
                  {condition.conditionOperator === CounterpartyOperator.IS ||
                  condition.conditionOperator ===
                    CounterpartyOperator.IS_ANY_OF ? (
                    <>
                      {condition.conditionOperator ===
                      CounterpartyOperator.IS ? (
                        <Autocomplete
                          options={counterparties || []}
                          getOptionLabel={(option) => option.name}
                          value={condition.values[0] || null}
                          onChange={(_, value) => {
                            handleConditionValueChange(
                              index,
                              value ? [value] : []
                            );
                            markConditionTouched(index);
                          }}
                          onBlur={() => handleAutocompleteBlur(index)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={`${translate(
                                'labels.selectCounterpartyPlaceholder'
                              )}*`}
                              size="small"
                              error={hasError(index, 'values')}
                            />
                          )}
                        />
                      ) : (
                        <Autocomplete
                          multiple
                          options={counterparties || []}
                          getOptionLabel={(option) => option.name}
                          value={condition.values || []}
                          onChange={(_, value) => {
                            handleConditionValueChange(index, value || []);
                            markConditionTouched(index);
                          }}
                          onBlur={() => handleAutocompleteBlur(index)}
                          renderValue={(selected) =>
                            selected.map(
                              (option: { id: number; name: string }) => (
                                <Chip
                                  key={option.id}
                                  label={option.name}
                                  size="small"
                                  sx={{
                                    color: '#078dee',
                                    borderRadius: '0.25rem'
                                  }}
                                />
                              )
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={`${translate(
                                'labels.selectCounterpartiesPlaceholder'
                              )}*`}
                              size="small"
                              error={hasError(index, 'values')}
                            />
                          )}
                          sx={{ width: '100%' }}
                        />
                      )}
                    </>
                  ) : null}
                </Box>
              )}

              {condition.conditionType === ConditionType.AMOUNT && (
                <Box display="flex" alignItems="center" gap={1} flex={2}>
                  {condition.conditionOperator === AmountOperator.BETWEEN ? (
                    <>
                      <TextField
                        type="number"
                        placeholder="0"
                        size="small"
                        value={condition.values[0] || ''}
                        onChange={(e) =>
                          handleAmountValueChange(index, 0, e.target.value)
                        }
                        onBlur={() => handleAmountFieldBlur(index, 0)}
                        error={hasError(index, 'amountFrom')}
                      />
                      <Typography variant="body2">
                        {translate('labels.and')}
                      </Typography>
                      <TextField
                        type="number"
                        placeholder="0"
                        size="small"
                        value={condition.values[1] || ''}
                        onChange={(e) =>
                          handleAmountValueChange(index, 1, e.target.value)
                        }
                        onBlur={() => handleAmountFieldBlur(index, 1)}
                        error={hasError(index, 'amountTo')}
                      />
                      <FormControl size="small">
                        <Select
                          value={condition.values[2] || defaultCurrencyIsoCode}
                          onChange={(e: SelectChangeEvent) =>
                            handleAmountValueChange(index, 2, e.target.value)
                          }
                        >
                          {currencies?.map((currency) => (
                            <MenuItem
                              key={currency.id}
                              value={currency.isoCode}
                            >
                              {currency.isoCode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  ) : (
                    <>
                      <TextField
                        type="number"
                        placeholder="0"
                        size="small"
                        value={condition.values[0] || ''}
                        onChange={(e) =>
                          handleAmountValueChange(index, 0, e.target.value)
                        }
                        onBlur={() => handleAmountFieldBlur(index, 0)}
                        error={hasError(index, 'amountFrom')}
                      />
                      <FormControl size="small">
                        <Select
                          value={condition.values[1] || defaultCurrencyIsoCode}
                          onChange={(e: SelectChangeEvent) =>
                            handleAmountValueChange(index, 1, e.target.value)
                          }
                        >
                          {currencies?.map((currency) => (
                            <MenuItem
                              key={currency.id}
                              value={currency.isoCode}
                            >
                              {currency.isoCode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}
                </Box>
              )}
              <Close
                sx={{
                  color: '#d1d5db',
                  cursor: 'pointer',
                  '&:hover': { color: '#ef4444' }
                }}
                onClick={() => handleRemoveCondition(index)}
              />
            </Box>
          ))}
        </Box>
      )}
      {!isFallback && (
        <Box
          sx={{
            padding: '0 0.875rem 0.875rem 0.875rem',
            backgroundColor: 'white',
            borderBottomLeftRadius: '16px',
            borderBottomRightRadius: '16px'
          }}
        >
          <Button
            color="primary"
            onClick={handleAddCondition}
            startIcon={<Add />}
            sx={{ fontSize: '12px', padding: '0.3125rem 0.625rem' }}
          >
            {translate('approvals.addCondition')}
          </Button>
        </Box>
      )}
    </Paper>
  );
};
